import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FiChevronDown, FiChevronUp, FiRefreshCw, FiSearch, FiX, FiTarget, FiTrendingUp, FiMapPin } from 'react-icons/fi';
import { faturamentoService, filterDataService } from '../../services/api';

import { BIMultiSelectSearch, BIPeriodFilter, BIFilterSelect, BIPagination } from '../common';
import Loading from '../common/Loading';
const MultiSelectSearch = BIMultiSelectSearch;

function Monitoramento() {
  const [monitoring, setMonitoring] = useState([]);
  const [goals, setGoals] = useState([]);
  const [presets, setPresets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [progressAnimated, setProgressAnimated] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState(null);
  const pageRef = useRef(page);
  const buildFilterParamsRef = useRef(() => ({}));

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setProgressAnimated(true), 150);
      return () => clearTimeout(timer);
    }
    setProgressAnimated(false);
  }, [loading]);

  const defaultPeriod = (() => {
    const now = new Date();
    const day = now.getDate();
    let year = now.getFullYear();
    let month = now.getMonth() + 1;
    if (day <= 8) {
      month -= 1;
      if (month === 0) { month = 12; year -= 1; }
    }
    const pad = (n) => String(n).padStart(2, '0');
    const end = new Date(year, month, 0);
    return {
      monthValue: `${year}-${pad(month)}`,
      period_start: `${year}-${pad(month)}-01`,
      period_end: `${year}-${pad(month)}-${pad(end.getDate())}`
    };
  })();

  const [filterSelectedMonth, setFilterSelectedMonth] = useState(defaultPeriod.monthValue);
  const [searchTerm, setSearchTerm] = useState('');

  const [filters, setFilters] = useState({
    status: [],
    branch_ids: [],
    vendor_ids: [],
    employee_ids: [],
    goal_ids: [],
    target_type: '',
    goal_type: '',
    goal_classification: '',
    period_start: defaultPeriod.period_start,
    period_end: defaultPeriod.period_end,
    finalized: 'no'
  });

  const getMonthDates = (year, month) => {
    const end = new Date(year, month, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return {
      period_start: `${year}-${pad(month)}-01`,
      period_end: `${year}-${pad(month)}-${pad(end.getDate())}`
    };
  };

  const handleFilterMonthChange = (value) => {
    setFilterSelectedMonth(value);
    if (!value) {
      setFilters((prev) => ({ ...prev, period_start: '', period_end: '' }));
      return;
    }
    const [year, month] = value.split('-').map(Number);
    const dates = getMonthDates(year, month);
    setFilters((prev) => ({ ...prev, ...dates }));
  };

  const handleApplyPeriodPreset = (presetKey) => {
    const curMonthVal = filterSelectedMonth || defaultPeriod.monthValue;
    const [year, month] = curMonthVal ? curMonthVal.split('-').map(Number) : [new Date().getFullYear(), new Date().getMonth() + 1];
    const pad = (n) => String(n).padStart(2, '0');
    const lastDay = new Date(year, month, 0).getDate();

    let start = '';
    let end = '';

    if (presetKey === 'month') {
      start = `${year}-${pad(month)}-01`;
      end = `${year}-${pad(month)}-${pad(lastDay)}`;
    } else if (presetKey === '1q') {
      start = `${year}-${pad(month)}-01`;
      end = `${year}-${pad(month)}-15`;
    } else if (presetKey === '2q') {
      start = `${year}-${pad(month)}-16`;
      end = `${year}-${pad(month)}-${pad(lastDay)}`;
    } else if (presetKey === 'yesterday') {
      const today = new Date();
      today.setDate(today.getDate() - 1);
      start = `${year}-${pad(month)}-01`;
      end = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    }

    if (start && end) {
      setFilters((prev) => ({ ...prev, period_start: start, period_end: end }));
    }
  };

  const loadData = useCallback(async (filterParams = {}, silent = false) => {
    if (!silent) setLoading(true);

    let monData = [];
    let goalsData = [];
    let presetsData = [];
    let branchesData = [];
    let reportsData = [];

    try {
      const monitoringRes = await faturamentoService.getMonitoring(filterParams);
      monData = monitoringRes?.data?.monitoring || [];
      setTotalRecords(monitoringRes?.data?.total || 0);
    } catch (e) {
      console.error('Erro ao carregar monitoramento:', e);
    }

    try {
      const goalsParams = { per_page: 10000 };
      if (filterParams.period_start) {
        goalsParams.period_start = filterParams.period_start;
        goalsParams.start_date = filterParams.period_start;
      }
      if (filterParams.period_end) {
        goalsParams.period_end = filterParams.period_end;
        goalsParams.end_date = filterParams.period_end;
      }
      const goalsRes = await faturamentoService.listGoals(goalsParams);
      goalsData = goalsRes?.data?.goals || [];
    } catch (e) {
      console.error('Erro ao carregar metas:', e);
    }

    try {
      const presetsRes = await faturamentoService.listGoalPresets({ active: 1, per_page: 0 });
      presetsData = presetsRes?.data?.presets || [];
    } catch (e) {
      console.error('Erro ao carregar grupos de metas:', e);
    }

    try {
      const branchesRes = await filterDataService.getFiliais();
      branchesData = branchesRes?.data?.filiais || [];
    } catch (e) {
      console.error('Erro ao carregar filiais:', e);
    }

    let employeesData = [];
    try {
      const employeesRes = await faturamentoService.listEmployees({ per_page: 10000, ativo: 1 });
      employeesData = employeesRes?.data?.employees || [];
    } catch (e) {
      console.error('Erro ao carregar funcionários:', e);
    }

     try {
       const reportsParams = { per_page: 10000 };
       if (filterParams.period_start) {
         reportsParams.period_start = filterParams.period_start;
         reportsParams.start_date = filterParams.period_start;
       }
       if (filterParams.period_end) {
         reportsParams.period_end = filterParams.period_end;
         reportsParams.end_date = filterParams.period_end;
       }
       const reportsRes = await faturamentoService.listReports(reportsParams);
       reportsData = reportsRes?.data?.reports || [];
     } catch (e) {
       console.error('Erro ao carregar relatórios:', e);
     }

    setMonitoring(monData);
    setGoals(goalsData);
    setPresets(presetsData);

    const uniqueVendors = [];
    const seenVendors = new Set();
    goalsData.forEach(g => {
      if (g.vendor_id && !seenVendors.has(g.vendor_id)) {
        seenVendors.add(g.vendor_id);
        uniqueVendors.push({
          id: g.vendor_id,
          nome: g.vendor_name || `Vendedor #${g.vendor_id}`,
          branch_code: g.vendor_branch || g.vendor_codfilial || g.branch_code || ''
        });
      }
    });
    monData.forEach(g => {
      if (g.vendor_id && !seenVendors.has(g.vendor_id)) {
        seenVendors.add(g.vendor_id);
        uniqueVendors.push({
          id: g.vendor_id,
          nome: g.vendor_name || `Vendedor #${g.vendor_id}`,
          branch_code: g.vendor_branch || g.vendor_codfilial || g.branch_code || ''
        });
      }
    });
    setVendors(uniqueVendors);

    setBranches(branchesData.map(b => ({ id: String(b.codigo), label: `${b.codigo} - ${b.nomefantasia || b.razaosocial || 'Sem nome'}` })));

    setEmployees(employeesData.map(e => ({
      id: e.id,
      nome: e.nome,
      codfunc: e.codfunc,
      branch_code: e.codfilial ? String(e.codfilial) : ''
    })));

    const allReportsFromMon = [];
    monData.forEach(g => {
      if (g.reports && Array.isArray(g.reports)) {
        g.reports.forEach(r => {
          allReportsFromMon.push({ id: r.id, name: r.name || `Relatório #${r.id}` });
        });
      }
    });
    const allReportsFromApi = reportsData.map(r => ({ id: r.id, name: r.name || `Relatório #${r.id}` }));
    const seenReports = new Set();
    const uniqueReports = [];
    [...allReportsFromMon, ...allReportsFromApi].forEach(r => {
      if (!seenReports.has(r.id)) {
        seenReports.add(r.id);
        uniqueReports.push(r);
      }
    });
    setReports(uniqueReports);

    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    loadData(buildFilterParams(1));
    const interval = setInterval(() => {
      loadData(buildFilterParamsRef.current(pageRef.current), true);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const filtersLoaded = useRef(false);
  useEffect(() => {
    if (!filtersLoaded.current) {
      filtersLoaded.current = true;
      return;
    }
    setPage(1);
    pageRef.current = 1;
    const timer = setTimeout(() => {
      loadData(buildFilterParams(1));
    }, 400);
    return () => clearTimeout(timer);
  }, [filters, searchTerm]);

  const buildFilterParams = (pageNum = 1, currentPerPage = perPage) => {
    const params = { page: pageNum, per_page: currentPerPage };
    if (filters.status.length > 0) params.status = filters.status.join(',');
    if (filters.branch_ids.length > 0) params.branch = filters.branch_ids.join(',');
    if (filters.vendor_ids.length > 0) params.vendor_id = filters.vendor_ids.join(',');
    if (filters.employee_ids.length > 0) params.employee_id = filters.employee_ids.join(',');
    if (filters.goal_ids.length > 0) params.goal_id = filters.goal_ids.join(',');
    if (filters.target_type) params.target_type = filters.target_type;
    if (filters.goal_type) params.goal_type = filters.goal_type;
    if (filters.goal_classification) params.goal_classification = filters.goal_classification;
    if (searchTerm) params.search = searchTerm;
    if (filters.period_start) {
      params.period_start = filters.period_start;
      params.start_date = filters.period_start;
    }
    if (filters.period_end) {
      params.period_end = filters.period_end;
      params.end_date = filters.period_end;
    }
    if (filters.finalized) params.finalized = filters.finalized;
    return params;
  };
  buildFilterParamsRef.current = buildFilterParams;

  const applyFilters = () => {
    setPage(1);
    pageRef.current = 1;
    loadData(buildFilterParams(1));
  };

  const clearFilters = () => {
    setFilters({
      status: [],
      branch_ids: [],
      vendor_ids: [],
      employee_ids: [],
      goal_ids: [],
      target_type: '',
      goal_type: '',
      goal_classification: '',
      period_start: '',
      period_end: '',
      finalized: ''
    });
    setSearchTerm('');
    setFilterSelectedMonth('');
    setPage(1);
    pageRef.current = 1;
    loadData({ page: 1, per_page: perPage });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    pageRef.current = newPage;
    loadData({ ...buildFilterParams(newPage), page: newPage });
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
    pageRef.current = 1;
    loadData({ ...buildFilterParams(1, newPerPage), page: 1, per_page: newPerPage });
  };

  const totalPages = Math.ceil(totalRecords / perPage) || 1;

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const getGoalTypeLabel = (type) => {
    const map = { monthly_revenue: 'Mensal', quarterly_revenue: 'Trimestral', yearly_revenue: 'Anual' };
    return map[type] || type || '-';
  };

  const getGoalOptionLabel = useCallback((g) => {
    let name = g.vendor_name || g.employee_name;
    if (!name && g.preset_id && presets.length > 0) {
      const p = presets.find(pr => Number(pr.id) === Number(g.preset_id));
      if (p) name = p.name;
    }
    if (!name && g.preset_name) {
      name = g.preset_name;
    }
    if (!name) {
      name = g.branch_name ? `Filial: ${g.branch_name}` : (g.branch_code ? `Filial ${g.branch_code}` : (g.notes || 'Meta Geral'));
    }
    return `${name} - ${getGoalTypeLabel(g.goal_type)} ${g.reference_month ? `${g.reference_month}/` : ''}${g.reference_year}`;
  }, [presets]);

  const filteredGoalsOptions = useMemo(() => {
    if (!filters.branch_ids || filters.branch_ids.length === 0) {
      return goals;
    }
    const selectedBranches = filters.branch_ids.map(String);
    return goals.filter(g => {
      if (g.branch_code) {
        const goalBranches = String(g.branch_code).split(',').map(s => s.trim());
        if (goalBranches.some(b => selectedBranches.includes(b))) return true;
      }
      if (g.vendor_branch || g.vendor_codfilial) {
        const vBranch = String(g.vendor_branch || g.vendor_codfilial).trim();
        if (selectedBranches.includes(vBranch)) return true;
      }
      if (g.employee_branch || g.employee_codfilial) {
        const eBranch = String(g.employee_branch || g.employee_codfilial).trim();
        if (selectedBranches.includes(eBranch)) return true;
      }
      if (g.preset_id && presets.length > 0) {
        const p = presets.find(pr => Number(pr.id) === Number(g.preset_id));
        if (p && p.filter_branch) {
          const pBranches = String(p.filter_branch).split(',').map(s => s.trim());
          if (pBranches.some(b => selectedBranches.includes(b))) return true;
        }
      }
      if (!g.vendor_id && !g.employee_id && presets.length > 0) {
        const gVarIds = Array.isArray(g.variable_ids) ? g.variable_ids.map(Number) : (g.variable_ids ? String(g.variable_ids).split(',').map(Number) : []);
        const matched = presets.find(p => {
          if (!p.filter_branch) return false;
          const pVarIds = p.variable_ids_array || (p.variable_ids ? String(p.variable_ids).split(',').map(Number) : []);
          return pVarIds.some(vid => gVarIds.includes(vid));
        });
        if (matched && matched.filter_branch) {
          const pBranches = String(matched.filter_branch).split(',').map(s => s.trim());
          if (pBranches.some(b => selectedBranches.includes(b))) return true;
        }
      }
      return false;
    });
  }, [goals, filters.branch_ids, presets]);

  const filteredVendors = useMemo(() => {
    if (!filters.branch_ids || filters.branch_ids.length === 0) {
      return vendors;
    }
    const selectedBranches = filters.branch_ids.map(String);
    return vendors.filter(v => {
      if (!v.branch_code) return true;
      return selectedBranches.includes(String(v.branch_code).trim());
    });
  }, [vendors, filters.branch_ids]);

  const filteredEmployees = useMemo(() => {
    if (!filters.branch_ids || filters.branch_ids.length === 0) {
      return employees;
    }
    const selectedBranches = filters.branch_ids.map(String);
    return employees.filter(e => {
      if (!e.branch_code) return true;
      return selectedBranches.includes(String(e.branch_code).trim());
    });
  }, [employees, filters.branch_ids]);

  // === Funções de cálculo (espelho do Relatorios.js) ===
  const isDirectGoalFilter = (f, report) => {
    if (f.is_direct_goal_filter !== undefined && f.is_direct_goal_filter !== null) {
      return Boolean(Number(f.is_direct_goal_filter));
    }
    if (f.is_variable_placeholder !== undefined && f.is_variable_placeholder !== null) {
      return !Boolean(Number(f.is_variable_placeholder));
    }
    if (report?.processed_data?.calculations?.length > 0) {
      const isUsedInVar = report.processed_data.calculations.some(c => {
        let details = c.calculation_details;
        if (typeof details === 'string') {
          try { details = JSON.parse(details); } catch (e) { details = {}; }
        }
        return (details?.placeholders || []).some(ph =>
          Number(ph.filter_id) === Number(f.filter_id) ||
          (ph.dep_placeholders || []).some(dph => Number(dph.filter_id) === Number(f.filter_id))
        );
      });
      if (isUsedInVar) {
        return false;
      }
    }
    return true;
  };

  const getDirectReportFilters = (report) => {
    const list = report?.processed_data?.by_filter;
    if (!Array.isArray(list) || list.length === 0) return [];
    return list.filter(f => isDirectGoalFilter(f, report));
  };

  const getReportGoalTotal = (report) => {
    if (['produtos_destaque', 'clientes_positivados'].includes(report?.goal_subtype)) {
      if (report.goal_subtype === 'clientes_positivados') {
        return Number(report.processed_data?.totals?.total_orders ?? report.total_orders ?? 0);
      }
      return Number(report.processed_data?.totals?.total_quantity ?? report.total_quantity ?? 0);
    }
    if (report?.processed_data) {
      if (report.processed_data.totals?.goal_total !== undefined) {
        return Number(report.processed_data.totals.goal_total);
      }
      const isVarOnly = report.processed_data.totals?.is_variable_only ?? (
        !(getDirectReportFilters(report).some(f => (f.include_in_goal_total ?? 1) !== 0)) &&
        (report.processed_data.calculations?.length || 0) > 0
      );
      if (isVarOnly) {
        let total = 0;
        if (report.processed_data.calculations) {
          for (const c of report.processed_data.calculations) {
            if ((c.include_in_goal_total ?? 1) !== 0) {
              total += Number(c.result || 0);
            }
          }
        }
        return total;
      }
      let total = 0;
      const directFilters = getDirectReportFilters(report);
      for (const f of directFilters) {
        if ((f.include_in_goal_total ?? 1) !== 0) {
          total += Number(f.total_net || 0);
        }
      }
      if (report.processed_data.calculations) {
        for (const c of report.processed_data.calculations) {
          if ((c.include_in_goal_total ?? 1) !== 0) {
            total += Number(c.result || 0);
          }
        }
      }
      return total;
    }
    return report?.goal_total || 0;
  };

  const getReportGoalNet = (report) => {
    if (report?.processed_data) {
      if (report.processed_data.totals?.is_variable_only) {
        return 0;
      }
      const directFilters = getDirectReportFilters(report);
      if (directFilters.length > 0) {
        return directFilters.reduce((s, f) => {
          if ((f.include_in_goal_total ?? 1) !== 0) {
            return s + Number(f.total_net || 0);
          }
          return s;
        }, 0);
      }
      return Number(report.processed_data.totals?.total_net_revenue || 0);
    }
    return report?.total_net || 0;
  };

  const calcGoalBaseNet = (goal) => {
    if (getGoalIsVariableOnly(goal)) return 0;
    if (goal.reports && goal.reports.length > 0 && goal.reports.some(r => r.processed_data)) {
      return goal.reports.reduce((s, r) => s + getReportGoalNet(r), 0);
    }
    return goal.total_net || 0;
  };

  const getReportTotalNet = (report) => {
    if (report?.processed_data) {
      const directFilters = getDirectReportFilters(report);
      if (directFilters.length > 0) {
        return directFilters.reduce((s, f) => s + Number(f.total_net || 0), 0);
      }
      return Number(report.processed_data.totals?.total_net_revenue || 0);
    }
    return report?.total_net || 0;
  };

  const getReportTotalGross = (report) => {
    if (report?.processed_data) {
      const directFilters = getDirectReportFilters(report);
      if (directFilters.length > 0) {
        return directFilters.reduce((s, f) => s + Number(f.total_gross || 0), 0);
      }
      return Number(report.processed_data.totals?.total_gross_revenue || 0);
    }
    return report?.total_gross || 0;
  };

  const getReportTotalOrders = (report) => {
    if (report?.goal_subtype === 'produtos_destaque') {
      return Number(report?.processed_data?.totals?.total_quantity ?? report?.total_quantity ?? report?.processed_data?.totals?.total_orders ?? report?.total_orders ?? 0);
    }
    if (report?.processed_data) {
      const directFilters = getDirectReportFilters(report);
      if (directFilters.length > 0) {
        return directFilters.reduce((s, f) => s + Number(f.order_count || 0), 0);
      }
      return Number(report.processed_data.totals?.total_orders || 0);
    }
    return report?.total_orders || 0;
  };

  const getReportVariablesTotal = (report) => {
    return Number(report?.processed_data?.totals?.variables_total || report?.processed_data?.totals?.calculations_total || report?.goal_total || 0);
  };

  // Agregação por goal (com fallback para valores pré-calculados do backend)
  const calcGoalTotal = (goal) => {
    if (isQuantityGoal(goal)) {
      return getProgressAchieved(goal);
    }
    if (goal.reports && goal.reports.length > 0 && goal.reports.some(r => r.processed_data)) {
      return goal.reports.reduce((s, r) => s + getReportGoalTotal(r), 0);
    }
    return goal.total_achieved || 0;
  };

  const calcGoalGross = (goal) => {
    if (goal.reports && goal.reports.length > 0 && goal.reports.some(r => r.processed_data)) {
      return goal.reports.reduce((s, r) => s + getReportTotalGross(r), 0);
    }
    return goal.total_gross || 0;
  };

  const calcGoalOrders = (goal) => {
    if (goal.reports && goal.reports.length > 0 && goal.reports.some(r => r.processed_data)) {
      return goal.reports.reduce((s, r) => s + getReportTotalOrders(r), 0);
    }
    return goal.total_orders || 0;
  };

  const calcGoalVariables = (goal) => {
    if (goal.reports && goal.reports.length > 0 && goal.reports.some(r => r.processed_data)) {
      return goal.reports.reduce((s, r) => s + getReportVariablesTotal(r), 0);
    }
    return goal.total_achieved || 0;
  };

  const isQuantityGoal = (goal) => {
    const qtySubtypes = ['clientes_positivados', 'produtos_destaque'];
    return qtySubtypes.includes(goal?.goal_subtype);
  };

  const getGoalTarget = (goal) => {
    if (isQuantityGoal(goal)) {
      return Number(goal.target_quantity || 0);
    }
    return Number(goal.target_amount || 0);
  };

  const getProgressAchieved = (goal) => {
    if (isQuantityGoal(goal)) {
      if (goal?.goal_subtype === 'clientes_positivados') {
        if ((goal.reports || []).some(r => r.processed_data)) {
          return (goal.reports || []).reduce((sum, r) => sum + Number(r?.processed_data?.totals?.total_orders ?? r?.total_orders ?? 0), 0);
        }
        return Number(goal.total_orders || 0);
      }
      if (goal?.goal_subtype === 'produtos_destaque') {
        if ((goal.reports || []).some(r => r.processed_data)) {
          return (goal.reports || []).reduce((sum, r) => sum + Number(r?.processed_data?.totals?.total_quantity ?? r?.total_quantity ?? 0), 0);
        }
        return Number(goal.total_quantity ?? goal.total_orders ?? 0);
      }
      // Relatórios com processed_data: usa total_quantity dos filtros diretos ou totals
      if ((goal.reports || []).some(r => r.processed_data)) {
        return (goal.reports || []).reduce((sum, r) => {
          const directFilters = getDirectReportFilters(r);
          if (directFilters.length > 0) {
            return sum + directFilters.reduce((s, f) => s + Number(f.total_quantity || 0), 0);
          }
          return sum + Number(r?.processed_data?.totals?.total_quantity ?? r?.total_quantity ?? 0);
        }, 0);
      }
      // Fallback: usa total_quantity ou total_orders do goal
      return Number(goal.total_quantity ?? goal.total_orders ?? 0);
    }
    return calcGoalTotal(goal);
  };

  const calcGoalProgressPct = (goal) => {
    const target = getGoalTarget(goal);
    const achieved = getProgressAchieved(goal);
    if (target > 0) {
      return Math.min((achieved / target) * 100, 100);
    }
    return 0;
  };

  const getProgressColor = (goal) => {
    const pct = calcGoalProgressPct(goal);
    if (pct >= 100) return '#22c55e';
    if (pct >= 66) return '#84cc16';
    if (pct >= 33) return '#f97316';
    return '#fbbf24';
  };

  const getProgressGradient = () => {
    return 'linear-gradient(90deg, #fbbf24, #f97316, #22c55e)';
  };

  const getGoalHasFilterBreakdown = (goal) => {
    return (goal.reports || []).some(r => getDirectReportFilters(r).length > 0);
  };

  const getGoalIsVariableOnly = (goal) => {
    if (goal?.is_variable_only !== undefined) {
      return Boolean(goal.is_variable_only);
    }
    if ((goal?.variable_ids || (goal?.variables && goal.variables.length > 0)) && !goal?.filter_id && (!goal?.filters || goal.filters.length === 0)) {
      return true;
    }
    if (goal?.reports && goal.reports.length > 0) {
      return goal.reports.every(r => {
        if (r?.processed_data?.totals?.is_variable_only !== undefined) {
          return Boolean(r.processed_data.totals.is_variable_only);
        }
        const hasDirectFilters = getDirectReportFilters(r).some(
          f => (f.include_in_goal_total ?? 1) !== 0
        );
        return !hasDirectFilters && ((r?.processed_data?.calculations?.length || 0) > 0 || (r?.processed_data?.totals?.variables_total || 0) > 0);
      });
    }
    return false;
  };
  const getFilterValidationConfig = (phOrFilter, goal, reportFilters = []) => {
    if (!phOrFilter) {
      return {
        filterId: 0,
        isCustom: false,
        commMinQty: 0,
        bonusMinQty: 0,
        targetQty: 0,
        commMinValue: 0,
        bonusMinValue: 0,
        targetValue: 0,
        useGoalValue: false
      };
    }
    const filterId = Number(phOrFilter.filter_id || phOrFilter.id || 0);
    const goalConfigs = goal?.filter_configs;
    const goalConfig = (goalConfigs && filterId > 0) ? (goalConfigs[filterId] || goalConfigs[String(filterId)]) : null;

    let matchedFilter = null;
    if (filterId > 0 && Array.isArray(reportFilters)) {
      matchedFilter = reportFilters.find(f => Number(f.filter_id || f.id) === filterId);
    }
    if (!matchedFilter && (phOrFilter.filter_name || phOrFilter.name) && Array.isArray(reportFilters)) {
      const rawName = phOrFilter.filter_name || phOrFilter.name;
      const matchName = (a, b) => {
        if (!a || !b) return false;
        const na = a.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const nb = b.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        return na === nb || na.includes(nb) || nb.includes(na);
      };
      matchedFilter = reportFilters.find(f => matchName(f.filter_name || f.name, rawName));
    }

    const isCustom = Boolean(
      (goalConfig && (goalConfig.is_custom !== undefined ? Number(goalConfig.is_custom) === 1 || goalConfig.is_custom === true : true)) ||
      Boolean(Number(phOrFilter.is_custom_for_goal || phOrFilter.is_custom || 0)) ||
      Boolean(Number(matchedFilter?.is_custom_for_goal || matchedFilter?.is_custom || 0))
    );

    // Target Qty: commission_min_products_qty prioritário, fallback para bonus_min_products_qty
    const commMinQty = Number(
      goalConfig?.commission_min_products_qty ??
      phOrFilter.filter_commission_min_products_qty ??
      phOrFilter.commission_min_products_qty ??
      matchedFilter?.commission_min_products_qty ??
      0
    );

    const bonusMinQty = Number(
      goalConfig?.bonus_min_products_qty ??
      phOrFilter.bonus_min_products_qty ??
      matchedFilter?.bonus_min_products_qty ??
      0
    );

    const targetQty = commMinQty > 0 ? commMinQty : (bonusMinQty > 0 ? bonusMinQty : 0);

    // Target Value: commission_min_sold_value ou meta
    let commMinValue = Number(
      goalConfig?.commission_min_sold_value ??
      phOrFilter.filter_commission_min_sold_value ??
      phOrFilter.commission_min_sold_value ??
      matchedFilter?.commission_min_sold_value ??
      0
    );

    const useGoalValue = Boolean(
      Number(goalConfig?.commission_use_goal_value ?? 0) === 1 ||
      Number(phOrFilter.filter_commission_use_goal_value ?? 0) === 1 ||
      Number(phOrFilter.commission_use_goal_value ?? 0) === 1 ||
      Number(matchedFilter?.commission_use_goal_value ?? 0) === 1
    );

    if (useGoalValue && Number(goal?.target_amount || 0) > 0) {
      commMinValue = Number(goal.target_amount);
    }

    const bonusMinValue = Number(
      goalConfig?.bonus_min_sold_value ??
      phOrFilter.bonus_min_sold_value ??
      matchedFilter?.bonus_min_sold_value ??
      0
    );

    const targetValue = commMinValue > 0 ? commMinValue : (bonusMinValue > 0 ? bonusMinValue : 0);

    return {
      filterId,
      isCustom,
      commMinQty,
      bonusMinQty,
      targetQty,
      commMinValue,
      bonusMinValue,
      targetValue,
      useGoalValue
    };
  };

  const calculateFilterProgress = (f, goal) => {
    const config = getFilterValidationConfig(f, goal, []);
    const qty = Number(f.total_quantity || 0);
    const net = Number(f.total_net || 0);

    const pcts = [];

    if (config.commMinQty > 0) {
      pcts.push((qty / config.commMinQty) * 100);
    }
    if (config.commMinValue > 0) {
      pcts.push((net / config.commMinValue) * 100);
    }
    if (config.bonusMinQty > 0) {
      pcts.push((qty / config.bonusMinQty) * 100);
    }
    if (config.bonusMinValue > 0) {
      pcts.push((net / config.bonusMinValue) * 100);
    }

    if (pcts.length === 0) return null;
    return Math.min(Math.max(...pcts), 100);
  };

  const getVariablePlaceholders = (c) => {
    const result = { filterPlaceholders: [], varPlaceholders: [] };
    if (!c.calculation_details) return result;
    try {
      const details = typeof c.calculation_details === 'string'
        ? JSON.parse(c.calculation_details)
        : c.calculation_details;
      const placeholders = details?.placeholders || [];
      const collectPlaceholders = (phs) => {
        for (const ph of phs) {
          if (ph.type === 'variable') {
            result.varPlaceholders.push(ph);
          } else {
            result.filterPlaceholders.push(ph);
          }
          if (ph.dep_placeholders) {
            collectPlaceholders(ph.dep_placeholders);
          }
        }
      };
      collectPlaceholders(placeholders);
    } catch (e) {}
    return result;
  };

  const calculatePlaceholderProgress = (ph, c, goal, filters = []) => {
    const config = getFilterValidationConfig(ph, goal, filters);
    const qty = Number(ph.filter_sold_qty || 0);
    const net = Number(ph.net || 0);

    const pcts = [];

    if (config.commMinQty > 0) {
      pcts.push((qty / config.commMinQty) * 100);
    }
    if (config.commMinValue > 0) {
      pcts.push((net / config.commMinValue) * 100);
    }
    if (config.bonusMinQty > 0) {
      pcts.push((qty / config.bonusMinQty) * 100);
    }
    if (config.bonusMinValue > 0) {
      pcts.push((net / config.bonusMinValue) * 100);
    }

    if (pcts.length === 0 && c) {
      let phMinValue = Number(c.commission_min_sold_value || 0);
      if (c.commission_use_goal_value && Number(goal?.target_amount || 0) > 0) {
        phMinValue = Number(goal.target_amount);
      }
      if (phMinValue > 0) {
        pcts.push((net / phMinValue) * 100);
      }
    }

    if (pcts.length === 0) return null;
    return Math.min(Math.max(...pcts), 100);
  };

  const calculateVariableProgress = (c, goal, filters = []) => {
    if (c.calculation_details) {
      try {
        const details = typeof c.calculation_details === 'string'
          ? JSON.parse(c.calculation_details)
          : c.calculation_details;

        if (details?.use_sold_qty_for_bonus && Number(details.bonus_validation_qty || 0) > 0) {
          const totalSold = Number(details.total_sold_qty || 0);
          const reqQty = Number(details.bonus_validation_qty);
          return Math.min((totalSold / reqQty) * 100, 100);
        }

        const placeholders = details?.placeholders || [];
        const allFilterPhs = [];
        const collectFilterPlaceholders = (phs) => {
          for (const ph of phs) {
            if (ph.type !== 'variable') allFilterPhs.push(ph);
            if (ph.dep_placeholders) collectFilterPlaceholders(ph.dep_placeholders);
          }
        };
        collectFilterPlaceholders(placeholders);
        const pcts = [];
        for (const ph of allFilterPhs) {
          const pct = calculatePlaceholderProgress(ph, c, goal, filters);
          if (pct !== null) pcts.push(pct);
        }
        if (pcts.length > 0) {
          const avg = pcts.reduce((sum, val) => sum + val, 0) / pcts.length;
          return Math.min(avg, 100);
        }
      } catch (e) {}
    }
    if (filters.length > 0 && c.variable_name) {
      const matchName = (a, b) => {
        if (!a || !b) return false;
        const na = a.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const nb = b.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        return na === nb || na.includes(nb) || nb.includes(na);
      };
      const matchingFilter = filters.find(f => matchName(f.filter_name, c.variable_name));
      if (matchingFilter) {
        const pct = calculateFilterProgress(matchingFilter, goal);
        if (pct !== null) return pct;
      }
    }
    let minValue = Number(c.commission_min_sold_value || 0);
    if (c.commission_use_goal_value && Number(goal?.target_amount || 0) > 0) {
      minValue = Number(goal.target_amount);
    }
    if (minValue <= 0) return null;
    const result = Number(c.result || 0);
    return Math.min((result / minValue) * 100, 100);
  };

  const getProgressBarColor = (pct) => {
    if (pct >= 100) return '#22c55e';
    if (pct >= 66) return '#84cc16';
    if (pct >= 33) return '#f97316';
    return '#fbbf24';
  };

  const renderProgressBar = (pct) => {
    if (pct === null || pct === undefined) {
      return <span style={{ color: '#94a3b8', fontSize: '0.65rem' }}>-</span>;
    }
    const color = getProgressBarColor(pct);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <div style={{ flex: 1, height: '7px', borderRadius: '4px', background: '#e9ecef', overflow: 'hidden', minWidth: '50px' }}>
          <div style={{
            height: '100%',
            width: progressAnimated ? `${pct}%` : '0%',
            background: color,
            borderRadius: '4px',
            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }} />
        </div>
        <span style={{ fontSize: '0.62rem', fontWeight: 700, color, minWidth: '30px', textAlign: 'right' }}>{Math.round(pct)}%</span>
      </div>
    );
  };

  const isPlaceholderActive = (ph, goal, filters = []) => {
    const qty = Number(ph.filter_sold_qty || 0);
    if (qty <= 0) return false;
    const net = Number(ph.net || 0);
    const config = getFilterValidationConfig(ph, goal, filters);
    const hasAnyMin = config.commMinQty > 0 || config.commMinValue > 0 || config.bonusMinQty > 0 || config.bonusMinValue > 0;
    if (!hasAnyMin) return true;
    if (config.commMinQty > 0 && qty >= config.commMinQty) return true;
    if (config.commMinValue > 0 && net >= config.commMinValue) return true;
    if (config.bonusMinQty > 0 && qty >= config.bonusMinQty) return true;
    if (config.bonusMinValue > 0 && net >= config.bonusMinValue) return true;
    return false;
  };

  // === Fim funções de cálculo ===

  return (
    <div className="container-fluid py-4">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Monitoramento</h2>
          <p className="text-muted mb-0">Acompanhamento do andamento das metas e progresso dos relatórios.</p>
        </div>
        <button type="button" className="btn-modern btn-modern-light" onClick={() => loadData(buildFilterParams(page))} disabled={loading}>
          <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} />
        </button>
      </div>

      {/* Painel de Filtros Padronizado no Estilo BI */}
      <div className="bi-toolbar mb-4" style={{ overflow: 'visible', position: 'relative', zIndex: 30 }}>
        <div className="bi-filters-left">
          {/* Status */}
          <div style={{ minWidth: '150px' }}>
            <MultiSelectSearch
              label="Status" placeholder="Buscar..."
              value={filters.status}
              onChange={(selected) => setFilters({ ...filters, status: selected })}
              options={[
                { id: 'draft', label: 'Pendente' },
                { id: 'processing', label: 'Processando' },
                { id: 'completed', label: 'Processado' },
                { id: 'failed', label: 'Falhou' }
              ]}
              badgeColor="#3b82f6"
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
            />
          </div>

          {/* Filial */}
          <div style={{ minWidth: '160px' }}>
            <MultiSelectSearch
              label="Filial" placeholder="Buscar filial..."
              value={filters.branch_ids}
              onChange={(selected) => setFilters({ ...filters, branch_ids: selected })}
              options={branches}
              badgeColor="#f59e0b"
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
            />
          </div>

          {/* Vendedor */}
          <div style={{ minWidth: '170px' }}>
            <MultiSelectSearch
              label="Vendedor" placeholder="Buscar vendedor..."
              value={filters.vendor_ids}
              onChange={(selected) => setFilters({ ...filters, vendor_ids: selected })}
              options={filteredVendors.map(v => ({ id: v.id, label: v.nome }))}
              badgeColor="#10b981"
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
            />
          </div>

          {/* Funcionário */}
          <div style={{ minWidth: '170px' }}>
            <MultiSelectSearch
              label="Funcionário" placeholder="Buscar funcionário..."
              value={filters.employee_ids}
              onChange={(selected) => setFilters({ ...filters, employee_ids: selected })}
              options={filteredEmployees.map(e => ({ id: e.id, label: e.nome }))}
              badgeColor="#8b5cf6"
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
            />
          </div>

          {/* Meta */}
          <div style={{ minWidth: '180px' }}>
            <MultiSelectSearch
              label="Meta" placeholder="Buscar meta..."
              value={filters.goal_ids}
              onChange={(selected) => setFilters({ ...filters, goal_ids: selected })}
              options={filteredGoalsOptions.map(g => ({
                id: g.id,
                label: getGoalOptionLabel(g)
              }))}
              badgeColor="#6f42c1"
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
            />
          </div>

          {/* Finalizado */}
          <BIFilterSelect
            label="Finalizado"
            value={filters.finalized}
            onChange={(val) => setFilters({ ...filters, finalized: val })}
            options={[
              { value: 'no', label: 'Não' },
              { value: 'yes', label: 'Sim' }
            ]}
            allLabel="Todos"
            minWidth="120px"
          />

          {/* Alvo */}
          <BIFilterSelect
            label="Alvo"
            value={filters.target_type || ''}
            onChange={(val) => setFilters({ ...filters, target_type: val })}
            options={[
              { value: 'vendor', label: 'Vendedor' },
              { value: 'employee', label: 'Funcionário' },
              { value: 'branch', label: 'Filial' },
              { value: 'general', label: 'Geral' }
            ]}
            allLabel="Todos os Alvos"
            minWidth="145px"
          />

          {/* Tipo */}
          <BIFilterSelect
            label="Tipo"
            value={filters.goal_type || ''}
            onChange={(val) => setFilters({ ...filters, goal_type: val })}
            options={[
              { value: 'monthly_revenue', label: 'Mensal' },
              { value: 'quarterly_revenue', label: 'Trimestral' },
              { value: 'yearly_revenue', label: 'Anual' },
              { value: 'clientes_positivados', label: 'Clientes Positivados' },
              { value: 'produtos_destaque', label: 'Produtos Destaque' }
            ]}
            allLabel="Todos os Tipos"
            minWidth="155px"
          />

          {/* Classificação */}
          <BIFilterSelect
            label="Classificação"
            value={filters.goal_classification || ''}
            onChange={(val) => setFilters({ ...filters, goal_classification: val })}
            options={[
              { value: 'vendedor', label: 'Vendedor' },
              { value: 'bijou', label: 'Bijou' },
              { value: 'operador_caixa', label: 'Operador de Caixa' }
            ]}
            allLabel="Todas as Classificações"
            minWidth="155px"
          />

          {/* Período no formato BI */}
          <BIPeriodFilter
            startDate={filters.period_start}
            endDate={filters.period_end}
            onChange={(start, end) => setFilters({ ...filters, period_start: start, period_end: end })}
            label="Início / Fim"
            minWidth="195px"
          />

          {/* Buscar */}
          <div className="bi-filter-item" style={{ flex: 1, minWidth: '220px' }}>
            <span className="bi-filter-label">Buscar</span>
            <div className="bi-search-input-wrapper position-relative">
              <FiSearch
                size={15}
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', zIndex: 2, pointerEvents: 'none' }}
              />
              <input
                type="text"
                className="form-control-modern bi-input w-100"
                placeholder="Buscar por nome, vendedor, meta, período..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '40px', paddingRight: searchTerm ? '32px' : '12px' }}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="bi-search-clear-btn"
                  onClick={() => setSearchTerm('')}
                  title="Limpar busca"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Barra de Ações */}
        <div className="d-flex align-items-center gap-2 mt-3 pt-2 border-top w-100">
          <button type="button" className="btn btn-primary d-flex align-items-center gap-2 px-3" style={{ height: '38px', borderRadius: '8px', fontWeight: 600, fontSize: '0.84rem' }} onClick={applyFilters}>
            <FiSearch size={14} /> Aplicar Filtros
          </button>
          <button type="button" className="btn btn-light border d-flex align-items-center gap-2 px-3" style={{ height: '38px', borderRadius: '8px', fontWeight: 600, fontSize: '0.84rem', color: '#475569', backgroundColor: '#f8fafc' }} onClick={clearFilters}>
            <FiX size={14} /> Limpar
          </button>
        </div>
      </div>

      {/* Listagem em Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative', zIndex: 1 }}>
        {loading ? (
          <Loading
            text="Carregando dados de monitoramento..."
            subtext="Consolidando o acompanhamento do progresso de metas e faturamento..."
          />
        ) : monitoring.length === 0 ? (
          <div className="card-modern d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '200px' }}>
            <FiTarget size={48} color="#d1d5db" />
            <p className="text-muted mt-3 mb-0">Nenhum dado de monitoramento encontrado.</p>
            <small className="text-muted">Crie metas e relatórios para acompanhar o progresso.</small>
          </div>
        ) : (
          monitoring.map((goal) => (
            <div key={goal.goal_id}>
              <div className="card-modern" style={{ overflow: 'visible' }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="mb-0 fw-bold text-dark">
                      {goal.reports && goal.reports.length > 0 ? goal.reports[0].name : (goal.vendor_name || goal.employee_name || goal.preset_name || (goal.branch_name ? `Filial: ${goal.branch_name}` : (goal.notes || 'Todos os Colaboradores')))}
                      {goal.target_type && (
                        <span className="badge ms-2" style={{
                          fontSize: '0.68rem',
                          backgroundColor: goal.target_type === 'employee' ? '#f5f3ff' : (goal.target_type === 'branch' ? '#fef3c7' : (goal.target_type === 'general' ? '#f1f5f9' : '#ecfdf5')),
                          color: goal.target_type === 'employee' ? '#7c3aed' : (goal.target_type === 'branch' ? '#d97706' : (goal.target_type === 'general' ? '#475569' : '#059669')),
                          border: `1px solid ${goal.target_type === 'employee' ? '#ddd6fe' : (goal.target_type === 'branch' ? '#fde68a' : (goal.target_type === 'general' ? '#cbd5e1' : '#a7f3d0'))}`
                        }}>
                          {goal.target_type === 'employee' ? 'Funcionário' : (goal.target_type === 'branch' ? 'Filial' : (goal.target_type === 'general' ? 'Geral' : 'Vendedor'))}
                        </span>
                      )}
                    </h6>
                    <small className="text-muted">
                      {getGoalTypeLabel(goal.goal_type)} {goal.reference_month && `${goal.reference_month}/${goal.reference_year}`}
                      {(goal.employee_name && !goal.vendor_name && !goal.is_general) && <> &middot; {goal.employee_name}</>}
                      {(!goal.employee_name && !goal.vendor_name && goal.is_general) && <> &middot; <span className="badge bg-primary-subtle text-primary" style={{ fontSize: '0.68rem', fontWeight: 600 }}>{goal.preset_name || (goal.branch_name ? `Filial: ${goal.branch_name}` : 'Todos os Colaboradores')}</span></>}
                      {(goal.branch_name || goal.branch_code) && (
                        <> &middot; <span className="badge bg-warning-subtle text-warning" style={{ fontSize: '0.68rem', fontWeight: 600, border: '1px solid #fde68a' }}>
                          <FiMapPin size={10} className="me-1" />{goal.branch_name ? goal.branch_name : `Filial ${goal.branch_code}`}
                        </span></>
                      )}
                      {goal.goal_classification && (
                        <> &middot; <span style={{
                          fontSize: '0.68rem', padding: '1px 7px', borderRadius: '10px',
                          fontWeight: 600,
                          backgroundColor: goal.goal_classification === 'bijou' ? '#fdf2f8' : (goal.goal_classification === 'operador_caixa' ? '#fffbeb' : '#eff6ff'),
                          color: goal.goal_classification === 'bijou' ? '#db2777' : (goal.goal_classification === 'operador_caixa' ? '#d97706' : '#2563eb'),
                          border: `1px solid ${goal.goal_classification === 'bijou' ? '#fbcfe8' : (goal.goal_classification === 'operador_caixa' ? '#fde68a' : '#bfdbfe')}`
                        }}>
                          {goal.goal_classification === 'bijou' ? 'Bijou' : (goal.goal_classification === 'operador_caixa' ? 'Operador de Caixa' : 'Vendedor')}
                        </span></>
                      )}
                      {((goal.variables && goal.variables.some(v => v.has_accrual_rule || (v.accrual_rules && v.accrual_rules.length > 0))) ||
                        (goal.reports && goal.reports.some(r => r.processed_data?.calculations?.some(c => {
                          try {
                            const d = JSON.parse(c.calculation_details || '{}');
                            return d.has_accrual_rule && (d.accrual_rules || []).length > 0;
                          } catch { return false; }
                        })))) && (
                        <> &middot; <span style={{
                          fontSize: '0.68rem', padding: '1px 7px', borderRadius: '10px',
                          fontWeight: 600,
                          backgroundColor: '#fffbeb',
                          color: '#b45309',
                          border: '1px solid #fde68a',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}>
                          <FiTrendingUp size={11} /> Regras de Acréscimo
                        </span></>
                      )}
                    </small>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {((goal.reports && goal.reports.some(r => r.processed_data)) || (goal.variables && goal.variables.length > 0) || (goal.filters && goal.filters.length > 0)) && (
                      <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => setExpandedGoalId(expandedGoalId === goal.goal_id ? null : goal.goal_id)} title="Expandir">
                        {expandedGoalId === goal.goal_id ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                      </button>
                    )}
                    {goal.latest_report_finalized && (
                      <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: '12px', background: '#dbeafe', color: '#2563eb', fontWeight: 600, flexShrink: 0 }}>Finalizado</span>
                    )}
                  </div>
                </div>

                {/* Barra de Progresso */}
                {getGoalTarget(goal) > 0 && getProgressAchieved(goal) >= 0 && (
                  <div className="mt-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.75rem', color: '#6b7280' }}>
                        {isQuantityGoal(goal) ? `${formatNumber(getProgressAchieved(goal))} ${goal.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatCurrency(getProgressAchieved(goal))} de {isQuantityGoal(goal) ? `${formatNumber(getGoalTarget(goal))} ${goal.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatCurrency(getGoalTarget(goal))}
                      </span>
                      <span style={{ fontWeight: '700', fontSize: '0.85rem', color: getProgressColor(goal) }}>
                        {calcGoalProgressPct(goal).toFixed(1)}%
                      </span>
                    </div>
                    <div style={{ height: '14px', borderRadius: '7px', background: '#e9ecef', width: '100%', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: progressAnimated ? `${Math.min(calcGoalProgressPct(goal), 100)}%` : '0%',
                        background: getProgressGradient(),
                        borderRadius: '7px',
                        transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                      }} />
                    </div>
                    {(getGoalHasFilterBreakdown(goal) || getGoalIsVariableOnly(goal) || isQuantityGoal(goal)) && (
                      <small className="text-muted d-block mt-1">
                        {isQuantityGoal(goal)
                          ? `Total: ${formatNumber(getProgressAchieved(goal))} de ${formatNumber(getGoalTarget(goal))} ${goal.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}`
                          : getGoalIsVariableOnly(goal)
                            ? (() => {
                                const excluded = calcGoalVariables(goal) - calcGoalTotal(goal);
                                return `Base: Variáveis (${formatCurrency(calcGoalVariables(goal))}) = ${formatCurrency(calcGoalTotal(goal))}` +
                                  (excluded > 0 ? ` (Itens excluídos: ${formatCurrency(excluded)})` : '');
                              })()
                            : `Base: Líquido (${formatCurrency(calcGoalBaseNet(goal))}) + Variáveis (${formatCurrency(calcGoalVariables(goal))}) = ${formatCurrency(calcGoalTotal(goal))}`
                        }
                      </small>
                    )}
                  </div>
                )}

                {/* Expandido — detalhes dos relatórios e variáveis */}
                {expandedGoalId === goal.goal_id && ((goal.reports && goal.reports.filter(r => r.processed_data).length > 0) || (goal.variables && goal.variables.length > 0)) && (
                  <div className="mt-3 pt-3 border-top">
                    {goal.reports && goal.reports.filter(r => r.processed_data).length > 0 && (
                      <h6 className="mb-3 text-dark fw-semibold" style={{ fontSize: '0.85rem' }}>Valores Obtidos do Sincronizador</h6>
                    )}
                    {goal.reports.filter(r => r.processed_data).map((report, rIdx) => (
                      <div key={rIdx} className="mb-4">
                        {goal.reports.length > 1 && (
                          <div className="fw-medium text-muted mb-2" style={{ fontSize: '0.8rem' }}>{report.name}</div>
                        )}

                        {/* Resultado para metas de quantidade */}
                        {isQuantityGoal(goal) && (
                          <>
                            <div className="bg-white p-3 rounded border mb-3" style={{ borderLeft: '4px solid #10b981' }}>
                              <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                {goal.goal_subtype === 'clientes_positivados' ? 'Clientes Positivados' : 'Total Alcançado'}
                              </small>
                              <div className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}>
                                {formatNumber(getProgressAchieved(goal))} de {formatNumber(getGoalTarget(goal))} {goal.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}
                              </div>
                            </div>
                            {['produtos_destaque', 'clientes_positivados'].includes(goal.goal_subtype) && (
                              <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                  <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid var(--info)' }}>
                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                      {goal.goal_subtype === 'clientes_positivados' ? 'Valor por Cliente' : 'Total Adicional'}
                                    </small>
                                    <div className="fw-semibold text-dark mt-1">
                                      {formatCurrency(
                                        Number(report?.processed_data?.totals?.unit_value ?? goal.unit_value ?? 0) *
                                        (goal.goal_subtype === 'clientes_positivados'
                                          ? Number(report?.processed_data?.totals?.total_orders ?? report?.total_orders ?? 0)
                                          : Number(report?.processed_data?.totals?.total_quantity ?? report?.total_quantity ?? 0))
                                      )}
                                    </div>
                                    {(() => {
                                      const uv = Number(report?.processed_data?.totals?.unit_value ?? goal.unit_value ?? 0);
                                      const tq = goal.goal_subtype === 'clientes_positivados'
                                        ? Number(report?.processed_data?.totals?.total_orders ?? report?.total_orders ?? 0)
                                        : Number(report?.processed_data?.totals?.total_quantity ?? report?.total_quantity ?? 0);
                                      if (uv > 0 && tq > 0) {
                                        return (
                                          <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                            {`${uv.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4 })} × ${formatNumber(tq)} ${goal.goal_subtype === 'clientes_positivados' ? 'clientes' : 'unidades'}`}
                                          </small>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid var(--success)' }}>
                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Adicional Configurado</small>
                                    <div className="fw-semibold text-dark mt-1">{formatCurrency(
                                      getProgressAchieved(goal) >= getGoalTarget(goal)
                                        ? Number(report?.processed_data?.totals?.goal_bonus_amount ?? goal.bonus_amount ?? 0)
                                        : 0
                                    )}</div>
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid #fd7e14' }}>
                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Adicional Obtido</small>
                                    <div className="fw-semibold text-dark mt-1">{formatCurrency(
                                      getProgressAchieved(goal) >= getGoalTarget(goal)
                                        ? (Number(report?.processed_data?.totals?.bonus_amount ?? report.bonus_amount ?? 0) +
                                           Number(report?.processed_data?.totals?.filter_commission_goal_total ?? 0))
                                        : 0
                                    )}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Totais do relatório */}
                        {!isQuantityGoal(goal) && (() => {
                          const directFilters = getDirectReportFilters(report);
                          const hasFilterBreakdown = directFilters.length > 0;
                          const totalOrders = hasFilterBreakdown
                            ? directFilters.reduce((s, f) => s + Number(f.order_count || 0), 0)
                            : Number(report.processed_data.totals?.total_orders || 0);
                          const totalGross = hasFilterBreakdown
                            ? directFilters.reduce((s, f) => s + Number(f.total_gross || 0), 0)
                            : Number(report.processed_data.totals?.total_gross_revenue || 0);
                          const totalVariables = Number(report.processed_data.totals?.variables_total || 0);

                          return (
                            <>
                              {/* Tabela de filtros */}
                              {directFilters.length > 0 && (
                                <div className="table-responsive mb-3" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', background: '#fff' }}>
                                    <thead>
                                      <tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'left' }}>Filtro</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>%</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Pedidos</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Qtd. Meta</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Qtd. Alcançada</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Devoluções</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Adicional</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Na Meta</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Progresso</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Bruto</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Líquido</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {directFilters.map((f, fIdx) => {
                                        const fConfig = getFilterValidationConfig(f, goal, directFilters);
                                        const soldQty = Number(f.total_quantity ?? 0);
                                        const targetQty = fConfig.targetQty;
                                        const reachedTarget = targetQty > 0 ? (soldQty >= targetQty) : (soldQty > 0);
                                        return (
                                          <tr key={fIdx} style={{ borderTop: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '7px 12px', color: '#334155' }}>
                                              <span>{f.filter_name || `Filtro #${f.filter_id}`}</span>
                                              {fConfig.isCustom && (
                                                <span
                                                  className="badge ms-1"
                                                  style={{ background: '#ecfdf5', color: '#047857', fontSize: '0.62rem', border: '1px solid #a7f3d0' }}
                                                  title="Este filtro possui valores de referência configurados exclusivamente para esta meta"
                                                >
                                                  ⚡ Exclusivo da Meta
                                                </span>
                                              )}
                                              {f.has_accrual_rule ? (
                                                <span className="badge ms-1" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.6rem', border: '1px solid #fde68a' }} title="Este filtro possui regra de acréscimo vinculada">
                                                  ⚡ Acréscimo
                                                </span>
                                              ) : null}
                                            </td>
                                            <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                                              {(f.show_commission_percentage === undefined || f.show_commission_percentage) && Number(f.commission_percent || 0) > 0 ? (
                                                <span style={{ fontSize: '0.65rem', padding: '2px 6px', borderRadius: '4px', background: '#f1f5f9', color: '#475569' }}>{formatNumber(f.commission_percent)}%</span>
                                              ) : '-'}
                                            </td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: '#334155' }}>{formatNumber(f.order_count)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: '#64748b' }}>
                                              {targetQty > 0 ? (
                                                <span style={{ fontWeight: 600, color: '#334155' }}>{formatNumber(targetQty)}</span>
                                              ) : (
                                                <span style={{ color: '#94a3b8' }}>-</span>
                                              )}
                                            </td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: reachedTarget ? '#16a34a' : '#334155', fontWeight: reachedTarget ? 600 : 400 }}>
                                              {formatNumber(soldQty)}
                                            </td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: '#dc2626' }}>{formatCurrency(f.total_returns ?? f.total_discount)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: '#334155' }}>{formatCurrency(f.commission_amount ?? 0)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'center', color: (f.include_in_goal_total ?? 1) ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                              {(f.include_in_goal_total ?? 1) ? 'Sim' : 'Não'}
                                            </td>
                                            <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                                              {renderProgressBar(calculateFilterProgress(f, goal))}
                                            </td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: '#334155' }}>{formatCurrency(f.total_gross)}</td>
                                            <td style={{ padding: '7px 12px', textAlign: 'right', color: '#334155', fontWeight: 600 }}>{formatCurrency(f.total_net)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#1e293b' }}>Total dos Filtros</td>
                                        <td></td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{formatNumber(directFilters.reduce((s, f) => s + Number(f.order_count || 0), 0))}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#64748b' }}>
                                          {(() => {
                                            const totalTarget = directFilters.reduce((s, f) => s + Number(getFilterValidationConfig(f, goal, directFilters).targetQty || 0), 0);
                                            return totalTarget > 0 ? formatNumber(totalTarget) : '-';
                                          })()}
                                        </td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{formatNumber(directFilters.reduce((s, f) => s + Number(f.total_quantity || 0), 0))}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(directFilters.reduce((s, f) => s + Number(f.total_returns || f.total_discount || 0), 0))}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(directFilters.reduce((s, f) => s + Number(f.commission_amount || 0), 0))}</td>
                                        <td></td>
                                        <td></td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(directFilters.reduce((s, f) => s + Number(f.total_gross || 0), 0))}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(directFilters.reduce((s, f) => s + Number(f.total_net || 0), 0))}</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              )}

                              {/* Tabela de variáveis */}
                              {report.processed_data.calculations?.length > 0 && (
                                <div className="table-responsive mb-3" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', background: '#fff' }}>
                                    <thead>
                                      <tr style={{ background: '#f8fafc' }}>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'left' }}>Variável</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>%</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Resultado</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Adicional</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Na Meta</th>
                                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Progresso</th>
                                       </tr>
                                     </thead>
                                      <tbody>
                                         {report.processed_data.calculations.map((c, cIdx) => {
                                           const { filterPlaceholders, varPlaceholders } = getVariablePlaceholders(c);
                                           const details = (() => { try { return JSON.parse(c.calculation_details || '{}'); } catch { return {}; } })();
                                           const allPlaceholders = details.placeholders || [];
                                           const filterNames = {};
                                           (report.processed_data.by_filter || []).forEach(f => { filterNames[f.filter_id] = f.filter_name; });
                                           let formulaWithValues = c.formula || '';
                                           allPlaceholders.forEach(ph => {
                                             formulaWithValues = formulaWithValues.replace(
                                               new RegExp(`\\{${ph.placeholder}\\}`, 'g'),
                                               formatCurrency(Number(ph.value || 0))
                                             );
                                           });
                                           const hasInner = filterPlaceholders.length > 0 || varPlaceholders.length > 0 || !!c.formula;
                                           return (
                                             <React.Fragment key={cIdx}>
                                               <tr style={{ borderTop: '1px solid #f1f5f9' }}>
                                                 <td style={{ padding: '7px 12px', color: '#334155' }}>
                                                   <div>{c.variable_name || `Variável #${c.variable_id}`}</div>
                                                   {(() => {
                                                     const det = (() => { try { return JSON.parse(c.calculation_details || '{}'); } catch { return {}; } })();
                                                     const rules = det.accrual_rules || [];
                                                     if (det.has_accrual_rule && rules.length > 0) {
                                                       const anyActivated = rules.some(r => r.activated);
                                                       const extraPct = rules.filter(r => r.activated).reduce((s, r) => s + Number(r.accrual_percent || 0), 0);
                                                       const extraAmount = rules.filter(r => r.activated).reduce((s, r) => s + Number(r.accrual_amount || 0), 0);
                                                       const parts = [];
                                                       if (extraPct > 0) parts.push(`+${formatNumber(extraPct)}%`);
                                                       if (extraAmount > 0) parts.push(`+${formatCurrency(extraAmount)}`);
                                                       const label = parts.length > 0 ? parts.join(' ') : '+0';
                                                       return (
                                                         <div style={{ marginTop: '2px' }}>
                                                           {anyActivated ? (
                                                             <span className="badge" style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.62rem', border: '1px solid #bbf7d0', fontWeight: 600 }}>
                                                               ✓ {label} Acréscimo
                                                             </span>
                                                           ) : (
                                                             <span className="badge" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.62rem', border: '1px solid #fde68a', fontWeight: 600 }}>
                                                               ⚡ Acréscimo ({rules.length})
                                                             </span>
                                                           )}
                                                         </div>
                                                       );
                                                     }
                                                     return null;
                                                   })()}
                                                 </td>
                                                 <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                                                   {(c.show_commission_percentage === undefined || c.show_commission_percentage) ? (
                                                     <span style={{ fontSize: '0.65rem', padding: '2px 6px', borderRadius: '4px', background: '#f1f5f9', color: '#475569' }}>{formatNumber(c.commission_percent)}%</span>
                                                   ) : '-'}
                                                 </td>
                                                 <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, color: '#334155' }}>{c.result === null || c.result === undefined ? '-' : formatCurrency(Number(c.result))}</td>
                                                 <td style={{ padding: '7px 12px', textAlign: 'right', color: '#334155' }}>{formatCurrency(c.commission_amount ?? 0)}</td>
                                                 <td style={{ padding: '7px 12px', textAlign: 'center', color: (c.include_in_goal_total ?? 1) ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                                   {(c.include_in_goal_total ?? 1) ? 'Sim' : 'Não'}
                                                 </td>
                                                  <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                                                    {renderProgressBar(calculateVariableProgress(c, goal, report.processed_data.by_filter))}
                                                  </td>
                                               </tr>
                                                {hasInner && (
                                                  <tr key={`${cIdx}-inner`} style={{ background: '#fafcff' }}>
                                                    <td colSpan={6} style={{ padding: '6px 12px 6px 28px' }}>
                                                      <div style={{ fontSize: '0.72rem' }}>
                                                        {c.formula && (
                                                          <>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                                              <span style={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem' }}>Fórmula:</span>
                                                              <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '3px', fontSize: '0.7rem', color: '#334155' }}>{c.formula}</code>
                                                            </div>
                                                            {formulaWithValues !== c.formula && (
                                                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                                                <span style={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem' }}>Cálculo:</span>
                                                                <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '3px', fontSize: '0.7rem', color: '#334155' }}>{formulaWithValues}</code>
                                                              </div>
                                                            )}
                                                          </>
                                                        )}
                                                        {filterPlaceholders.length > 0 && (
                                                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.68rem', marginTop: '4px' }}>
                                                            <thead>
                                                              <tr style={{ background: '#f1f5f9' }}>
                                                                <th style={{ padding: '4px 10px', fontWeight: 600, color: '#64748b', textAlign: 'left', fontSize: '0.66rem' }}>Filtro</th>
                                                                <th style={{ padding: '4px 10px', fontWeight: 600, color: '#64748b', textAlign: 'right', fontSize: '0.66rem' }}>Qtd. Meta</th>
                                                                <th style={{ padding: '4px 10px', fontWeight: 600, color: '#64748b', textAlign: 'right', fontSize: '0.66rem' }}>Qtd. Alcançada</th>
                                                                <th style={{ padding: '4px 10px', fontWeight: 600, color: '#64748b', textAlign: 'right', fontSize: '0.66rem' }}>Bruto</th>
                                                                <th style={{ padding: '4px 10px', fontWeight: 600, color: '#64748b', textAlign: 'right', fontSize: '0.66rem' }}>Líquido</th>
                                                                <th style={{ padding: '4px 10px', fontWeight: 600, color: '#64748b', textAlign: 'center', fontSize: '0.66rem' }}>Progresso</th>
                                                              </tr>
                                                            </thead>
                                                            <tbody>
                                                                 {filterPlaceholders.map((ph, phIdx) => {
                                                                  const phConfig = getFilterValidationConfig(ph, goal, report.processed_data.by_filter);
                                                                  const phPct = calculatePlaceholderProgress(ph, c, goal, report.processed_data.by_filter);
                                                                  const phActive = isPlaceholderActive(ph, goal, report.processed_data.by_filter);
                                                                  const accRule = (details.accrual_rules || []).find(r => r.reference_type === 'filter' && Number(r.reference_id) === Number(ph.filter_id));
                                                                  const soldQty = Number(ph.filter_sold_qty ?? 0);
                                                                  const targetQty = phConfig.targetQty;
                                                                  const reachedTarget = targetQty > 0 ? (soldQty >= targetQty) : (soldQty > 0);
                                                                 return (
                                                                   <tr key={phIdx} style={{ borderTop: '1px solid #f1f5f9', background: phActive ? '#f0fdf4' : 'transparent' }}>
                                                                     <td style={{ padding: '4px 10px', color: '#475569', fontSize: '0.66rem' }}>
                                                                       <span>{filterNames[ph.filter_id] || ph.filter_name || `Filtro #${ph.filter_id}`}</span>
                                                                       {(() => {
                                                                          if (!phConfig.isCustom) return null;
                                                                          const usesBase = ph.use_goal_filter_config === false || ph.use_goal_filter_config === 0;
                                                                          if (usesBase) {
                                                                            return (
                                                                              <span className="badge ms-1" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '0.58rem', border: '1px solid #e2e8f0' }} title="Calculado usando regras base do filtro">Filtro Base</span>
                                                                            );
                                                                          }
                                                                          return (
                                                                            <span className="badge ms-1" style={{ background: '#ecfdf5', color: '#047857', fontSize: '0.58rem', border: '1px solid #a7f3d0' }} title="Valores configurados exclusivamente para esta meta">⚡ Exclusivo da Meta</span>
                                                                          );
                                                                        })()}
                                                                       {accRule && (() => {
                                                                          const bonusParts = [];
                                                                          if (Number(accRule.accrual_percent || 0) > 0) bonusParts.push(`+${formatNumber(accRule.accrual_percent)}%`);
                                                                          if (Number(accRule.accrual_amount || 0) > 0) bonusParts.push(`+${formatCurrency(accRule.accrual_amount)}`);
                                                                          const bonusTxt = bonusParts.join(' ') || '+0';
                                                                          return (
                                                                            <span className="badge ms-1" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.58rem', border: '1px solid #fde68a' }} title={`Alvo de acréscimo (${bonusTxt})`}>
                                                                              ⚡ Acréscimo ({bonusTxt})
                                                                            </span>
                                                                          );
                                                                        })()}
                                                                     </td>
                                                                     <td style={{ padding: '4px 10px', textAlign: 'right', fontSize: '0.66rem', color: '#64748b' }}>
                                                                       {targetQty > 0 ? (
                                                                         <span style={{ fontWeight: 600, color: '#334155' }}>{formatNumber(targetQty)}</span>
                                                                       ) : (
                                                                         <span style={{ color: '#94a3b8' }}>-</span>
                                                                       )}
                                                                     </td>
                                                                     <td style={{ padding: '4px 10px', textAlign: 'right', fontSize: '0.66rem', color: reachedTarget ? '#16a34a' : '#6f42c1', fontWeight: 600 }}>
                                                                       {formatNumber(soldQty)}
                                                                     </td>
                                                                     <td style={{ padding: '4px 10px', textAlign: 'right', fontSize: '0.66rem', color: '#28a745' }}>{formatCurrency(ph.gross || 0)}</td>
                                                                     <td style={{ padding: '4px 10px', textAlign: 'right', fontSize: '0.66rem', color: '#17a2b8' }}>{formatCurrency(ph.net || 0)}</td>
                                                                     <td style={{ padding: '4px 10px', textAlign: 'center', fontSize: '0.66rem' }}>{renderProgressBar(phPct)}</td>
                                                                   </tr>
                                                                 );
                                                               })}
                                                            </tbody>
                                                          </table>
                                                        )}
                                                        {/* Regras de Acréscimo de Adicional */}
                                                        {(() => {
                                                          const det = (() => { try { return JSON.parse(c.calculation_details || '{}'); } catch { return {}; } })();
                                                          const rules = det.accrual_rules || [];
                                                          if (det.has_accrual_rule && rules.length > 0) {
                                                            const anyActivated = rules.some(r => r.activated);
                                                            const totalExtraPct = rules.filter(r => r.activated).reduce((s, r) => s + Number(r.accrual_percent || 0), 0);
                                                            const totalExtraAmount = rules.filter(r => r.activated).reduce((s, r) => s + Number(r.accrual_amount || 0), 0);
                                                            const parts = [];
                                                            if (totalExtraPct > 0) parts.push(`+${formatNumber(totalExtraPct)}%`);
                                                            if (totalExtraAmount > 0) parts.push(`+${formatCurrency(totalExtraAmount)}`);
                                                            const totalBonusTxt = parts.length > 0 ? parts.join(' ') : '+0';
                                                            return (
                                                              <div style={{
                                                                marginTop: '8px',
                                                                padding: '8px 10px',
                                                                background: '#fffdf5',
                                                                border: '1px solid #fde68a',
                                                                borderLeft: '4px solid #d97706',
                                                                borderRadius: '6px'
                                                              }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <FiTrendingUp size={13} style={{ color: '#d97706' }} />
                                                                    <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#92400e' }}>
                                                                      Regras de Acréscimo de Adicional:
                                                                    </span>
                                                                  </div>
                                                                  <span style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                                    fontSize: '0.66rem', padding: '2px 8px', borderRadius: '4px',
                                                                    background: anyActivated ? '#d4edda' : '#fef3c7',
                                                                    color: anyActivated ? '#155724' : '#92400e',
                                                                    border: anyActivated ? '1px solid #c3e6cb' : '1px solid #fde68a',
                                                                    fontWeight: 700
                                                                  }}>
                                                                    {anyActivated ? `✓ Acréscimo Ativado (${totalBonusTxt})` : `⚡ Pendente (${rules.length} regra${rules.length > 1 ? 's' : ''})`}
                                                                  </span>
                                                                </div>

                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    {rules.map((rule, rIdx) => {
                                                                      const hasQtyThreshold = Boolean(rule.threshold_quantity && Number(rule.threshold_quantity) > 0);
                                                                      const thresholdQty = hasQtyThreshold ? Number(rule.threshold_quantity) : 0;
                                                                      const currentQty = rule.reference_quantity != null ? Number(rule.reference_quantity) : 0;
                                                                      const thresholdVal = Number(rule.threshold || rule.threshold_value || 0);
                                                                      const currentVal = rule.reference_value != null ? Number(rule.reference_value) : null;

                                                                      // Percentuais de progresso
                                                                      const valPct = thresholdVal > 0 && currentVal !== null ? Math.min((currentVal / thresholdVal) * 100, 100) : (thresholdVal <= 0 ? 100 : 0);
                                                                      const qtyPct = hasQtyThreshold ? Math.min((currentQty / thresholdQty) * 100, 100) : 100;

                                                                      let rulePct = 0;
                                                                      if (rule.activated) {
                                                                        rulePct = 100;
                                                                      } else if (thresholdVal > 0 && hasQtyThreshold) {
                                                                        rulePct = Math.min(valPct, qtyPct);
                                                                      } else if (hasQtyThreshold) {
                                                                        rulePct = qtyPct;
                                                                      } else if (thresholdVal > 0) {
                                                                        rulePct = valPct;
                                                                      } else {
                                                                        rulePct = 0;
                                                                      }

                                                                      const refLabel = rule.reference_name || (rule.reference_type === 'filter' ? `Filtro #${rule.reference_id}` : rule.reference_type === 'report' ? `Relatório #${rule.reference_id}` : `Variável #${rule.reference_id}`);
                                                                      const refTypeLabel = rule.reference_type === 'variable' ? 'Variável' : (rule.reference_type === 'filter' ? 'Filtro' : 'Relatório');
                                                                      const rParts = [];
                                                                      if (Number(rule.accrual_percent || 0) > 0) rParts.push(`+${formatNumber(rule.accrual_percent)}%`);
                                                                      if (Number(rule.accrual_amount || 0) > 0) rParts.push(`+${formatCurrency(rule.accrual_amount)}`);
                                                                      const rBonusTxt = rParts.join(' e ') || '+0';

                                                                      return (
                                                                        <div key={rIdx} style={{
                                                                          padding: '6px 8px',
                                                                          borderRadius: '4px',
                                                                          background: rule.activated ? '#f0fdf4' : '#ffffff',
                                                                          border: rule.activated ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                                                                          fontSize: '0.66rem',
                                                                          color: rule.activated ? '#155724' : '#334155'
                                                                        }}>
                                                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                              <span className="badge" style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.62rem', fontWeight: 600 }}>{refTypeLabel}</span>
                                                                              <strong style={{ color: rule.activated ? '#166534' : '#1e293b' }}>{refLabel}</strong>
                                                                            </div>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                              <span style={{ fontWeight: 700, color: rule.activated ? '#16a34a' : '#d97706', fontSize: '0.68rem' }}>
                                                                                → {rBonusTxt} Adicional
                                                                              </span>
                                                                              <span className="badge" style={{
                                                                                background: rule.activated ? '#dcfce7' : '#fef9c3',
                                                                                color: rule.activated ? '#15803d' : '#a16207',
                                                                                fontSize: '0.62rem',
                                                                                fontWeight: 600
                                                                              }}>
                                                                                {rule.activated ? '✓ Ativado' : '⚡ Em andamento'}
                                                                              </span>
                                                                            </div>
                                                                          </div>

                                                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                                                                            <span style={{ color: '#64748b' }}>
                                                                              {hasQtyThreshold && thresholdVal <= 0 ? (
                                                                                <>
                                                                                  Meta: <strong>{thresholdQty} un</strong>
                                                                                  &middot; Realizado: <strong style={{ color: rule.activated ? '#16a34a' : '#0284c7' }}>{currentQty} un</strong> ({qtyPct.toFixed(1)}%)
                                                                                  {currentVal !== null && <> &middot; Venda: <strong>{formatCurrency(currentVal)}</strong></>}
                                                                                </>
                                                                              ) : !hasQtyThreshold && thresholdVal > 0 ? (
                                                                                <>
                                                                                  Meta: <strong>{formatCurrency(thresholdVal)}</strong>
                                                                                  {currentVal !== null && (
                                                                                    <> &middot; Realizado: <strong style={{ color: rule.activated ? '#16a34a' : '#0284c7' }}>{formatCurrency(currentVal)}</strong> ({valPct.toFixed(1)}%)</>
                                                                                  )}
                                                                                </>
                                                                              ) : hasQtyThreshold && thresholdVal > 0 ? (
                                                                                <>
                                                                                  Meta: <strong>{formatCurrency(thresholdVal)}</strong>
                                                                                  {currentVal !== null && (
                                                                                    <> &middot; Realizado: <strong style={{ color: currentVal >= thresholdVal ? '#16a34a' : '#0284c7' }}>{formatCurrency(currentVal)}</strong> ({valPct.toFixed(1)}%)</>
                                                                                  )}
                                                                                  &middot; Qtd: <strong>{currentQty}</strong> de <strong>{thresholdQty} un</strong> ({qtyPct.toFixed(1)}%)
                                                                                </>
                                                                              ) : (
                                                                                <>
                                                                                  {currentVal !== null && <>Realizado: <strong>{formatCurrency(currentVal)}</strong></>}
                                                                                  {currentQty > 0 && <> &middot; Qtd: <strong>{currentQty} un</strong></>}
                                                                                </>
                                                                              )}
                                                                            </span>
                                                                            {!rule.activated && (() => {
                                                                              const missingParts = [];
                                                                              if (thresholdVal > 0 && currentVal !== null && currentVal < thresholdVal) {
                                                                                missingParts.push(formatCurrency(Math.max(0, thresholdVal - currentVal)));
                                                                              }
                                                                              if (hasQtyThreshold && currentQty < thresholdQty) {
                                                                                missingParts.push(`${thresholdQty - currentQty} un`);
                                                                              }
                                                                              if (missingParts.length === 0) return null;
                                                                              return (
                                                                                <span style={{ color: '#d97706', fontSize: '0.62rem' }}>
                                                                                  Falta {missingParts.join(' e ')} para liberar {rBonusTxt}
                                                                                </span>
                                                                              );
                                                                            })()}
                                                                          </div>

                                                                          {/* Barra de progresso da regra de acréscimo */}
                                                                          <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px', marginTop: '4px', overflow: 'hidden' }}>
                                                                            <div style={{
                                                                              height: '100%',
                                                                              width: `${Math.min(rulePct, 100)}%`,
                                                                              background: rule.activated ? '#22c55e' : '#f59e0b',
                                                                              borderRadius: '2px',
                                                                              transition: 'width 0.4s ease'
                                                                            }} />
                                                                          </div>
                                                                        </div>
                                                                      );
                                                                    })}
                                                                  </div>
                                                              </div>
                                                            );
                                                          }
                                                          return null;
                                                        })()}
                                                        {(() => {
                                                          const det = (() => { try { return JSON.parse(c.calculation_details || '{}'); } catch { return {}; } })();
                                                          if (det.use_sold_qty_for_bonus) {
                                                            const activated = det.qty_bonus_activated;
                                                            return (
                                                              <div style={{ marginTop: '6px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                                                                  <span style={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem' }}>Bonificação por Quantidade:</span>
                                                                  <span style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                                    fontSize: '0.65rem', padding: '1px 6px', borderRadius: '4px',
                                                                    background: activated ? '#d4edda' : '#fff3cd',
                                                                    color: activated ? '#155724' : '#856404',
                                                                    fontWeight: 600
                                                                  }}>
                                                                    {activated ? '✓ Ativada' : '✗ Não atingiu'}
                                                                  </span>
                                                                </div>
                                                                <div style={{ color: '#64748b', fontSize: '0.65rem', marginTop: '2px' }}>
                                                                  Qtd. vendida: <strong>{det.total_sold_qty ?? 0}</strong> {activated ? '≥' : '<'} <strong>{det.bonus_validation_qty}</strong> un → +<strong>{formatCurrency(det.variable_bonus_amount)}</strong>
                                                                </div>
                                                              </div>
                                                            );
                                                          }
                                                          return null;
                                                        })()}
                                                        {varPlaceholders.length > 0 && (
                                                          <div style={{ marginTop: '6px' }}>
                                                            <span style={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem' }}>Variáveis dependentes:</span>
                                                            {varPlaceholders.map((ph, phIdx) => {
                                                              const depRules = ph.dep_accrual_rules || [];
                                                              const hasDepAccrual = (ph.dep_has_accrual_rule || depRules.length > 0) && depRules.length > 0;
                                                              return (
                                                              <div key={phIdx} style={{
                                                                marginTop: '4px', padding: '6px 8px', background: '#f1f5f9',
                                                                borderRadius: '4px', borderLeft: '3px solid #6f42c1', fontSize: '0.68rem'
                                                              }}>
                                                                <div style={{ fontWeight: 600, color: '#475569', marginBottom: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                  <span>{ph.dep_var_name || `Variável #${ph.depends_on_variable_id}`}</span>
                                                                  {hasDepAccrual && (
                                                                    <span className="badge" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.6rem', border: '1px solid #fde68a' }}>
                                                                      ⚡ Acréscimo ({depRules.length})
                                                                    </span>
                                                                  )}
                                                                </div>
                                                                <div style={{ color: '#64748b', marginBottom: '2px' }}>
                                                                  Fórmula: <code style={{ background: '#e2e8f0', padding: '0 4px', borderRadius: '2px', fontSize: '0.65rem' }}>{ph.dep_formula}</code>
                                                                </div>
                                                                <div style={{ color: '#0d6efd' }}>
                                                                  Resultado: {formatCurrency(Number(ph.dep_result || 0))}
                                                                  <span style={{ color: '#64748b', marginLeft: '8px' }}>Adicional: {formatCurrency(Number(ph.dep_commission_amount || 0))}</span>
                                                                </div>
                                                                {hasDepAccrual && (
                                                                  <div style={{ marginTop: '4px', padding: '4px 6px', background: '#fffdf5', border: '1px solid #fde68a', borderRadius: '4px' }}>
                                                                    <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.62rem', marginBottom: '2px' }}>
                                                                      <FiTrendingUp size={10} className="me-1" /> Regras de Acréscimo da Dependência:
                                                                    </div>
                                                                    {depRules.map((dr, drIdx) => (
                                                                      <div key={drIdx} style={{ fontSize: '0.6rem', color: '#64748b' }}>
                                                                        &bull; {dr.reference_name || `Ref #${dr.reference_id}`} {dr.activated ? '≥' : '<'} {formatCurrency(dr.threshold || 0)} &rarr; {(() => {
                                                                          const parts = [];
                                                                          if (Number(dr.accrual_percent || 0) > 0) parts.push(`+${formatNumber(dr.accrual_percent)}%`);
                                                                          if (Number(dr.accrual_amount || 0) > 0) parts.push(`+${formatCurrency(dr.accrual_amount)}`);
                                                                          return parts.join(' e ') || '+0%';
                                                                        })()} {dr.activated && <strong style={{ color: '#16a34a' }}>✓</strong>}
                                                                      </div>
                                                                    ))}
                                                                  </div>
                                                                )}
                                                                {ph.dep_placeholders && ph.dep_placeholders.filter(dph => dph.type !== 'variable').length > 0 && (
                                                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.66rem', marginTop: '4px' }}>
                                                                    <thead>
                                                                      <tr style={{ background: '#e8ecf1' }}>
                                                                        <th style={{ padding: '3px 8px', fontWeight: 600, color: '#64748b', textAlign: 'left', fontSize: '0.64rem' }}>Filtro</th>
                                                                        <th style={{ padding: '3px 8px', fontWeight: 600, color: '#64748b', textAlign: 'right', fontSize: '0.64rem' }}>Qtd. Meta</th>
                                                                        <th style={{ padding: '3px 8px', fontWeight: 600, color: '#64748b', textAlign: 'right', fontSize: '0.64rem' }}>Qtd. Alcançada</th>
                                                                        <th style={{ padding: '3px 8px', fontWeight: 600, color: '#64748b', textAlign: 'right', fontSize: '0.64rem' }}>Bruto</th>
                                                                        <th style={{ padding: '3px 8px', fontWeight: 600, color: '#64748b', textAlign: 'right', fontSize: '0.64rem' }}>Líquido</th>
                                                                        <th style={{ padding: '3px 8px', fontWeight: 600, color: '#64748b', textAlign: 'center', fontSize: '0.64rem' }}>Progresso</th>
                                                                      </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {ph.dep_placeholders.filter(dph => dph.type !== 'variable').map((dph, dphIdx) => {
                                                                         const dphConfig = getFilterValidationConfig(dph, goal, report.processed_data.by_filter);
                                                                         const dphPct = calculatePlaceholderProgress(dph, c, goal, report.processed_data.by_filter);
                                                                         const dphActive = isPlaceholderActive(dph, goal, report.processed_data.by_filter);
                                                                         const soldQty = Number(dph.filter_sold_qty ?? 0);
                                                                         const targetQty = dphConfig.targetQty;
                                                                         const reachedTarget = targetQty > 0 ? (soldQty >= targetQty) : (soldQty > 0);
                                                                         return (
                                                                           <tr key={dphIdx} style={{ borderTop: '1px solid #e2e8f0', background: dphActive ? '#f0fdf4' : 'transparent' }}>
                                                                             <td style={{ padding: '3px 8px', color: '#475569', fontSize: '0.64rem' }}>
                                                                               <span>{filterNames[dph.filter_id] || dph.filter_name || `Filtro #${dph.filter_id}`}</span>
                                                                               {(() => {
                                                                                  if (!dphConfig.isCustom) return null;
                                                                                  const usesBase = dph.use_goal_filter_config === false || dph.use_goal_filter_config === 0;
                                                                                  if (usesBase) {
                                                                                    return (
                                                                                      <span className="badge ms-1" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '0.55rem', border: '1px solid #e2e8f0' }} title="Calculado usando regras base do filtro">Filtro Base</span>
                                                                                    );
                                                                                  }
                                                                                  return (
                                                                                    <span className="badge ms-1" style={{ background: '#ecfdf5', color: '#047857', fontSize: '0.55rem', border: '1px solid #a7f3d0' }} title="Valores configurados exclusivamente para esta meta">⚡ Exclusivo da Meta</span>
                                                                                  );
                                                                                })()}
                                                                             </td>
                                                                             <td style={{ padding: '3px 8px', textAlign: 'right', fontSize: '0.64rem', color: '#64748b' }}>
                                                                               {targetQty > 0 ? (
                                                                                 <span style={{ fontWeight: 600, color: '#334155' }}>{formatNumber(targetQty)}</span>
                                                                               ) : (
                                                                                 <span style={{ color: '#94a3b8' }}>-</span>
                                                                               )}
                                                                             </td>
                                                                             <td style={{ padding: '3px 8px', textAlign: 'right', fontSize: '0.64rem', color: reachedTarget ? '#16a34a' : '#6f42c1', fontWeight: 600 }}>
                                                                               {formatNumber(soldQty)}
                                                                             </td>
                                                                             <td style={{ padding: '3px 8px', textAlign: 'right', fontSize: '0.64rem', color: '#28a745' }}>{formatCurrency(dph.gross || 0)}</td>
                                                                             <td style={{ padding: '3px 8px', textAlign: 'right', fontSize: '0.64rem', color: '#17a2b8' }}>{formatCurrency(dph.net || 0)}</td>
                                                                             <td style={{ padding: '3px 8px', textAlign: 'center', fontSize: '0.64rem' }}>{renderProgressBar(dphPct)}</td>
                                                                           </tr>
                                                                         );
                                                                       })}
                                                                    </tbody>
                                                                  </table>
                                                                )}
                                                               </div>
                                                             );
                                                           })}
                                                          </div>
                                                        )}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                             </React.Fragment>
                                           );
                                         })}
                                      </tbody>
                                    <tfoot>
                                      <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#1e293b' }}>Total</td>
                                        <td></td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(report.processed_data.calculations.reduce((s, c) => s + Number(c.result || 0), 0))}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(report.processed_data.calculations.reduce((s, c) => s + Number(c.commission_amount || 0), 0))}</td>
                                        <td></td>
                                        <td></td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              )}

                              {!totalOrders && !totalGross && totalVariables === 0 && !report.processed_data.by_filter?.length && !report.processed_data.calculations?.length && (
                                <div className="text-muted small">Nenhum dado processado disponível.</div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ))}
                    {(!goal.reports || goal.reports.filter(r => r.processed_data).length === 0) && goal.variables && goal.variables.length > 0 && (
                      <div>
                        <h6 className="mb-3 text-dark fw-semibold" style={{ fontSize: '0.85rem' }}>
                          Variáveis Configuradas na Meta (Aguardando Sincronização)
                        </h6>
                        <div className="d-flex flex-column gap-2">
                          {goal.variables.map((v, vIdx) => (
                            <div key={vIdx} className="p-3 bg-white rounded border" style={{ fontSize: '0.72rem' }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <strong>{v.name}</strong>
                                <span className="badge bg-secondary-subtle text-secondary">{Number(v.commission_percent || 0).toFixed(2)}%</span>
                              </div>
                              <div className="text-muted mt-1">Fórmula: <code>{v.formula}</code></div>
                              {v.has_accrual_rule && v.accrual_rules && v.accrual_rules.length > 0 && (
                                <div className="mt-2 p-2 rounded" style={{ background: '#fffdf5', border: '1px solid #fde68a', borderLeft: '3px solid #d97706' }}>
                                  <div className="fw-semibold text-warning-emphasis mb-1" style={{ fontSize: '0.7rem' }}>
                                    <FiTrendingUp size={12} className="me-1" /> Regras de Acréscimo de Adicional ({v.accrual_rules.length}):
                                  </div>
                                  {v.accrual_rules.map((rule, rIdx) => {
                                     const rParts = [];
                                     if (Number(rule.accrual_percent || 0) > 0) rParts.push(`+${formatNumber(rule.accrual_percent)}%`);
                                     if (Number(rule.accrual_amount || 0) > 0) rParts.push(`+${formatCurrency(rule.accrual_amount)}`);
                                     const rBonusTxt = rParts.join(' e ') || '+0%';
                                     const rHasQty = Boolean(rule.threshold_quantity && Number(rule.threshold_quantity) > 0);
                                     const rThresholdVal = Number(rule.threshold || rule.threshold_value || 0);
                                     const condParts = [];
                                     if (rThresholdVal > 0) condParts.push(`≥ ${formatCurrency(rThresholdVal)}`);
                                     if (rHasQty) condParts.push(`≥ ${rule.threshold_quantity} un`);
                                     if (condParts.length === 0) condParts.push(`≥ R$ 0,00`);
                                     return (
                                       <div key={rIdx} style={{ fontSize: '0.66rem', color: '#64748b' }}>
                                         &bull; {rule.reference_name || (rule.reference_type === 'variable' ? `Variável #${rule.reference_id}` : rule.reference_type === 'filter' ? `Filtro #${rule.reference_id}` : `Relatório #${rule.reference_id}`)} {condParts.join(' e ')} &rarr; {rBonusTxt}
                                       </div>
                                     );
                                   })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Paginação */}
      <BIPagination
        page={page}
        perPage={perPage}
        total={totalRecords}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        itemLabel="metas"
        perPageOptions={[10, 25, 50, 100, 200]}
      />
    </div>
  );
}

export default Monitoramento;